import React, { forwardRef } from "react";
import { InputNumber, InputNumberProps, InputNumberValueChangeEvent } from "primereact/inputnumber";
import { classNames } from "primereact/utils";
import { DashlyFieldShell } from "../shared/DashlyFieldShell";
import { DashlyControlSize, dashlyControlSizeClass } from "../shared/dashlyControlSize";
import { useDashlyFieldValidation } from "../shared/useDashlyFieldValidation";
import { useDebouncedNumberValueChange } from "../shared/useDebouncedNumberValueChange";
import type { FieldValidationRules } from "../../../Util/fieldValidation";
import "../shared/dashlyFormControl.scss";
import "./DashlyNumberInput.scss";

export type DashlyNumberInputProps = InputNumberProps & {
    /** Debounce `onValueChange` (ms). Default 0 (immediate). */
    debounceMs?: number;
    /** Control height scale. Default `md` (2.5rem). */
    controlSize?: DashlyControlSize;
    label?: React.ReactNode;
    labelInfo?: string;
    hint?: React.ReactNode;
    required?: boolean;
    containerClassName?: string;
    /** Entity-aligned validation (required, min, max). */
    validation?: FieldValidationRules;
};

const DashlyNumberInput = forwardRef<InputNumber, DashlyNumberInputProps>(function DashlyNumberInput(
    {
        label,
        labelInfo,
        hint,
        required,
        containerClassName,
        validation,
        className,
        inputId,
        id,
        controlSize = "md",
        value,
        min,
        max,
        debounceMs = 0,
        onValueChange,
        onBlur,
        ...rest
    },
    ref
) {
    const { displayValue, handleValueChange: debouncedHandleValueChange, handleBlurFlush } =
        useDebouncedNumberValueChange({
            value,
            onValueChange,
            debounceMs,
        });
    const { error, invalid, markTouched } = useDashlyFieldValidation(
        debounceMs > 0 ? displayValue : value,
        validation,
    );
    const resolvedInputId = inputId ?? id;
    const effectiveMin = min ?? validation?.min;
    const effectiveMax = max ?? validation?.max;

    const handleValueChange = (event: InputNumberValueChangeEvent) => {
        markTouched();
        debouncedHandleValueChange(event);
    };

    const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
        markTouched();
        if (debounceMs > 0) {
            handleBlurFlush(displayValue ?? null);
        }
        onBlur?.(event);
    };

    const control = (
        <div
            className={classNames(
                "dashly-number-input-wrap",
                dashlyControlSizeClass(controlSize),
                invalid && "dashly-number-input-wrap--invalid",
            )}
        >
            <InputNumber
                ref={ref}
                {...rest}
                inputId={resolvedInputId}
                value={displayValue}
                min={effectiveMin}
                max={effectiveMax}
                aria-invalid={invalid || undefined}
                onValueChange={handleValueChange}
                onBlur={handleBlur}
                className={classNames("dashly-number-input", "w-full", invalid && "p-invalid", className)}
                inputClassName={classNames(
                    "dashly-number-input__field",
                    invalid && "p-invalid",
                    rest.inputClassName,
                )}
            />
        </div>
    );

    if (label || labelInfo || hint || error) {
        return (
            <DashlyFieldShell
                label={label}
                labelInfo={labelInfo}
                hint={hint}
                error={error}
                htmlFor={resolvedInputId}
                required={required ?? validation?.required}
                className={containerClassName}
            >
                {control}
            </DashlyFieldShell>
        );
    }

    if (error) {
        return (
            <div className={classNames("dashly-field-shell dashly-field-shell--invalid", containerClassName)}>
                {control}
                <span className="dashly-field-shell__error" role="alert">
                    {error}
                </span>
            </div>
        );
    }

    return control;
});

DashlyNumberInput.displayName = "DashlyNumberInput";

export { DashlyNumberInput };
